import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { devTrace } from "../services/devTrace";

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
  correlationId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true, correlationId: crypto.randomUUID() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    devTrace.record({
      runId: this.state.correlationId ?? "ui-error",
      step: "ui_error_boundary",
      actor: "React",
      level: "error",
      summary: "The UI encountered an unexpected rendering error.",
      rationale: "Captured by the global error boundary.",
      inputs: {
        errorName: error.name,
        componentStack: info.componentStack,
      },
    });
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <main className="fatal-error" role="alert">
        <AlertTriangle size={38} aria-hidden="true" />
        <h1>We could not display this page</h1>
        <p>
          The error was recorded with correlation ID{" "}
          <code>{this.state.correlationId}</code>.
        </p>
        <button type="button" className="button primary" onClick={() => location.reload()}>
          <RefreshCw size={17} aria-hidden="true" />
          Reload application
        </button>
      </main>
    );
  }
}
